import {
  Card,
  Form,
  Row,
  Col,
  Button,
  Switch,
  InputNumber,
  Alert,
  Modal,
  message,
} from "antd"
import {
  LeftOutlined,
  RightOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons"
import * as API from "common/api"
import * as COMMONS from "common/common"
import FullCalendar from "@fullcalendar/react"
import dayGridPlugin from "@fullcalendar/daygrid"
import interactionPlugin from "@fullcalendar/interaction"
import momentPlugin from "@fullcalendar/moment"
import moment from "moment"
import "moment/locale/ja"
import React, { useEffect, useRef, useState } from "react"
import { useQuery, useMutation, useQueryClient } from "react-query"
import { Helmet } from "react-helmet-async"
import { useNavigate } from "react-router-dom"
import styled from "styled-components"
import { ImCross } from "react-icons/im"

const CalendarWrapper = styled.div`
  a {
    color: inherit !important;
  }

  .fc .fc-button {
    padding: 0;
    background-color: #fff;
    color: ${COMMONS.PRIMARY_COLOR};
    border-radius: 0;
    border: 1px solid ${COMMONS.PRIMARY_COLOR};
    vertical-align: bottom;
    margin-bottom: 0.5rem;
  }

  .fc .fc-button-primary:not(:disabled):active:focus,
  .fc .fc-button-primary:not(:disabled).fc-button-active:focus {
    box-shadow: none;
  }

  .fc .fc-button-primary:focus {
    box-shadow: none;
  }

  .fc .fc-button-primary:not(:disabled):active,
  .fc .fc-button-primary:not(:disabled).fc-button-active {
    background-color: ${COMMONS.PRIMARY_COLOR};
    border-color: ${COMMONS.PRIMARY_COLOR};
  }

  .fc .fc-toolbar-title {
    font-weight: bold;
    font-size: 1.5em;
    color: ${COMMONS.PRIMARY_COLOR};
  }

  .fc .fc-daygrid-day {
    min-height: 70px;
  }

  .fc .fc-daygrid-day-frame {
    min-height: 70px;
  }

  .fc .fc-daygrid-body-unbalanced .fc-daygrid-day-events {
    min-height: 0;
  }

  .fc .fc-daygrid-body-natural .fc-daygrid-day-events {
    margin-bottom: 0;
  }

  .fc .fc-daygrid-day-events {
    margin-top: 0;
  }

  .fc .fc-daygrid-day-top {
    justify-content: center;
  }

  .fc .fc-daygrid-day-top .fc-daygrid-day-number:hover {
    color: #000 !important;
  }

  .fc .fc-daygrid-day.fc-day-today {
    background-color: #fff;
  }

  .fc .fc-daygrid-day-bottom {
    padding: 0;
  }

  .fc .fc-highlight {
    background: ${COMMONS.PRIMARY_COLOR};
  }

  .fc-CustomPrevMonth-button,
  .fc-CustomNextMonth-button {
    padding: 0.25rem 1rem !important;
    margin: 0.25rem !important;
  }

  .fc .fc-bg-event {
    opacity: 0.7;
    font-weight: bold;
  }
`

moment.updateLocale("ja")

const Pickup = (props) => {
  const navigate = useNavigate()

  const isMountedRef = COMMONS.USE_IS_MOUNTED_REF()
  const queryClient = useQueryClient()

  const [pickupLimitForm] = Form.useForm()
  const calendarHolidayRef = useRef()
  const calendarNoTasteDayRef = useRef()

  const [holidays, setHolidays] = useState([])
  const [noTasteDays, setNoTasteDays] = useState([])
  const [pickupLimit, setPickupLimit] = useState({})
  const [calendarFrom, setCalendarFrom] = useState(
    moment().startOf("month").hours(0).minutes(0).format("YYYY-MM-DD HH:mm"),
  )
  const [calendarTo, setCalendarTo] = useState(
    moment().endOf("month").hours(23).minutes(59).format("YYYY-MM-DD HH:mm"),
  )

  useQuery(
    [API.QUERY_KEY_ADMIN_HOLIDAYS, calendarFrom, calendarTo],
    () => API.ADMIN_GET_HOLIDAYS(calendarFrom, calendarTo),
    {
      onSuccess: (response) => {
        if (isMountedRef.current) {
          setHolidays(response?.data || [])
        }
      },
      onError: (error) => {
        if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
          navigate(COMMONS.PERMISSION_ERROR_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
          message.warning(COMMONS.ERROR_SESSION_MSG)
          navigate(COMMONS.ADMIN_LOGIN_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        } else {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        }
      },
    },
  )

  useQuery(
    [API.QUERY_KEY_ADMIN_NO_TASTE_DAYS, calendarFrom, calendarTo],
    () => API.ADMIN_GET_NO_TASTE_DAYS(calendarFrom, calendarTo),
    {
      onSuccess: (response) => {
        if (isMountedRef.current) {
          setNoTasteDays(response?.data || [])
        }
      },
      onError: (error) => {
        if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
          navigate(COMMONS.PERMISSION_ERROR_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
          message.warning(COMMONS.ERROR_SESSION_MSG)
          navigate(COMMONS.ADMIN_LOGIN_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        } else {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        }
      },
    },
  )

  useQuery(
    [API.QUERY_KEY_ADMIN_PICKUP_LIMIT],
    () => API.ADMIN_GET_PICKUP_LIMIT(),
    {
      onSuccess: (response) => {
        if (isMountedRef.current) {
          setPickupLimit(response?.data || {})
        }
      },
      onError: (error) => {
        if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
          navigate(COMMONS.PERMISSION_ERROR_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
          message.warning(COMMONS.ERROR_SESSION_MSG)
          navigate(COMMONS.ADMIN_LOGIN_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        } else {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        }
      },
    },
  )

  const holidayCreateMutation = useMutation(API.ADMIN_CREATE_HOLIDAY, {
    onSuccess: () => {
      message.success(COMMONS.SUCCESS_CREATE_MSG)
      queryClient.invalidateQueries(API.QUERY_KEY_ADMIN_HOLIDAYS)
    },
    onError: (error) => {
      if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
        navigate(COMMONS.PERMISSION_ERROR_ROUTE)
      } else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
        message.warning(COMMONS.ERROR_SESSION_MSG)
        navigate(COMMONS.ADMIN_LOGIN_ROUTE)
      } else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
        message.error(COMMONS.ERROR_SYSTEM_MSG)
      } else {
        message.error(COMMONS.ERROR_SYSTEM_MSG)
      }
    },
  })

  const noTasteDayCreateMutation = useMutation(API.ADMIN_CREATE_NO_TASTE_DAY, {
    onSuccess: () => {
      message.success(COMMONS.SUCCESS_CREATE_MSG)
      queryClient.invalidateQueries(API.QUERY_KEY_ADMIN_NO_TASTE_DAYS)
    },
    onError: (error) => {
      if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
        navigate(COMMONS.PERMISSION_ERROR_ROUTE)
      } else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
        message.warning(COMMONS.ERROR_SESSION_MSG)
        navigate(COMMONS.ADMIN_LOGIN_ROUTE)
      } else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
        message.error(COMMONS.ERROR_SYSTEM_MSG)
      } else {
        message.error(COMMONS.ERROR_SYSTEM_MSG)
      }
    },
  })

  const scheduleDeleteMutation = useMutation(API.ADMIN_DELETE_SCHEDULE, {
    onSuccess: () => {
      message.success(COMMONS.SUCCESS_DELETE_MSG)
      queryClient.invalidateQueries(API.QUERY_KEY_ADMIN_HOLIDAYS)
      queryClient.invalidateQueries(API.QUERY_KEY_ADMIN_NO_TASTE_DAYS)
    },
    onError: (error) => {
      if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
        navigate(COMMONS.PERMISSION_ERROR_ROUTE)
      } else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
        message.warning(COMMONS.ERROR_SESSION_MSG)
        navigate(COMMONS.ADMIN_LOGIN_ROUTE)
      } else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
        message.error(COMMONS.ERROR_SYSTEM_MSG)
      } else {
        message.error(COMMONS.ERROR_SYSTEM_MSG)
      }
    },
  })

  const pickupLimitMutation = useMutation(API.ADMIN_UPDATE_PICKUP_LIMIT, {
    onSuccess: () => {
      if (isMountedRef.current) {
        message.success(COMMONS.SUCCESS_SAVE_MSG)
        queryClient.invalidateQueries(API.QUERY_KEY_ADMIN_PICKUP_LIMIT)
      }
    },
    onError: (error) => {
      if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
        navigate(COMMONS.PERMISSION_ERROR_ROUTE)
      } else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
        message.warning(COMMONS.ERROR_SESSION_MSG)
        navigate(COMMONS.ADMIN_LOGIN_ROUTE)
      } else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
        message.error(COMMONS.ERROR_SYSTEM_MSG)
      } else {
        message.error(COMMONS.ERROR_SYSTEM_MSG)
      }
    },
  })

  useEffect(() => {
    pickupLimitForm.setFieldsValue({
      limitTime: pickupLimit?.layawayLimitTime || false,
      limitDay: pickupLimit?.layawayLimitDays || 0,
      limitHour: pickupLimit?.layawayLimitHours || 0,
      limitMinute: pickupLimit?.layawayLimitMinutes || 0,
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupLimit])

  const handlePickupLimit = (data) => {
    const paramData = {
      layawayLimitTime: data.limitTime,
      layawayLimitDays: data.limitTime ? data.limitDay : undefined,
      layawayLimitHours: data.limitTime ? data.limitHour : undefined,
      layawayLimitMinutes: data.limitTime ? data.limitMinute : undefined,
    }

    pickupLimitMutation.mutate(paramData)
  }

  const handleHolidayCreate = (date) => {
    const paramData = {
      type: "rest",
      date: moment(date).format("YYYY-MM-DD"),
      label: "",
    }

    holidayCreateMutation.mutate(paramData)
  }

  const handleNoTasteDayCreate = (date) => {
    const paramData = {
      type: "notaste",
      date: moment(date).format("YYYY-MM-DD"),
      label: "",
    }

    noTasteDayCreateMutation.mutate(paramData)
  }

  const handleScheduleDelete = (scheduleId) => {
    const paramData = {
      scheduleId: parseInt(scheduleId),
    }

    scheduleDeleteMutation.mutate(paramData)
  }

  const showCreateHolidayModal = (date) => {
    Modal.confirm({
      title: "休日登録",
      icon: <QuestionCircleOutlined style={{ color: COMMONS.PRIMARY_COLOR }} />,
      content: (
        <p>
          <span
            style={{ color: COMMONS.PRIMARY_COLOR }}
            className="font-bold"
          >{`${moment(date).format("YYYY年M月D日")}`}</span>
          の日を休日にしてもよろしいでしょうか？
        </p>
      ),
      okText: "登録",
      onOk: () => {
        handleHolidayCreate(date)
      },
      cancelText: "閉じる",
    })
  }

  const showCreateNoTasteDayModal = (date) => {
    Modal.confirm({
      title: "イートイン不可な日登録",
      icon: <QuestionCircleOutlined style={{ color: COMMONS.PRIMARY_COLOR }} />,
      content: (
        <p>
          <span
            style={{ color: COMMONS.PRIMARY_COLOR }}
            className="font-bold"
          >{`${moment(date).format("YYYY年M月D日")}`}</span>
          の日をイートイン不可な日にしてもよろしいでしょうか？
        </p>
      ),
      okText: "登録",
      onOk: () => {
        handleNoTasteDayCreate(date)
      },
      cancelText: "閉じる",
    })
  }

  const showDeleteHolidayModal = (date, scheduleId) => {
    Modal.confirm({
      title: "休日削除",
      icon: <QuestionCircleOutlined className="text-rose-600" />,
      content: (
        <p>
          <span className="text-rose-600 font-bold">{`${moment(date).format(
            "YYYY年M月D日",
          )}`}</span>
          の休日を削除してもよろしいでしょうか？
        </p>
      ),
      okText: "削除",
      okButtonProps: { danger: true },
      onOk: () => {
        handleScheduleDelete(scheduleId)
      },
      cancelText: "閉じる",
    })
  }

  const showDeleteNoTasteDayModal = (date, scheduleId) => {
    Modal.confirm({
      title: "イートイン不可な日削除",
      icon: <QuestionCircleOutlined className="text-rose-600" />,
      content: (
        <p>
          <span className="text-rose-600 font-bold">{`${moment(date).format(
            "YYYY年M月D日",
          )}`}</span>
          の試飲不可な日を削除してもよろしいでしょうか？
        </p>
      ),
      okText: "削除",
      okButtonProps: { danger: true },
      onOk: () => {
        handleScheduleDelete(scheduleId)
      },
      cancelText: "閉じる",
    })
  }

  return (
    <>
      <Helmet>
        <title>店舗受け取り設定 | {process.env.REACT_APP_SYSTEM_NAME}</title>
      </Helmet>
      <Card
        title="店舗受け取り設定"
        className="animate__animated animate__fadeIn"
        bordered={false}
      >
        <Card
          type="inner"
          title="予約時間限定"
          extra={
            <Button
              type="primary"
              size="large"
              className="w-32 m-1"
              onClick={() => {
                pickupLimitForm.submit()
              }}
            >
              保存
            </Button>
          }
        >
          <Form
            form={pickupLimitForm}
            layout="horizontal"
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            labelAlign="left"
            initialValues={{
              limitTime: false,
              limitDay: 0,
              limitHour: 3,
              limitMinute: 0,
            }}
            onFinish={handlePickupLimit}
            requiredMark={false}
            colon={false}
            size="large"
            scrollToFirstError
          >
            <Form.Item
              name="limitTime"
              label="予約時間制限"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => {
                return prevValues.limitTime !== currentValues.limitTime
              }}
            >
              {({ getFieldValue }) =>
                getFieldValue("limitTime") ? (
                  <>
                    <Form.Item label="予約より何日何時何分まで予約可能">
                      <Form.Item
                        name="limitDay"
                        rules={[
                          {
                            required: true,
                            message: "必須です",
                          },
                        ]}
                      >
                        <InputNumber
                          placeholder="例：1"
                          addonBefore="予約より"
                          addonAfter="日前"
                          min={0}
                          type="number"
                          onPressEnter={(e) => {
                            e.preventDefault()
                          }}
                        />
                      </Form.Item>
                      <Form.Item
                        name="limitHour"
                        rules={[
                          {
                            required: true,
                            message: "必須です",
                          },
                        ]}
                      >
                        <InputNumber
                          placeholder="例：1"
                          addonBefore="予約より"
                          addonAfter="時間前"
                          min={0}
                          max={23}
                          type="number"
                          onPressEnter={(e) => {
                            e.preventDefault()
                          }}
                        />
                      </Form.Item>
                      <Form.Item
                        name="limitMinute"
                        rules={[
                          {
                            required: true,
                            message: "必須です",
                          },
                        ]}
                      >
                        <InputNumber
                          placeholder="例：1"
                          addonBefore="予約より"
                          addonAfter="分前"
                          min={0}
                          max={59}
                          type="number"
                          onPressEnter={(e) => {
                            e.preventDefault()
                          }}
                        />
                      </Form.Item>
                    </Form.Item>
                  </>
                ) : (
                  ""
                )
              }
            </Form.Item>
          </Form>
        </Card>
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card type="inner" title="休日の設定" className="mt-8">
              <CalendarWrapper>
                <FullCalendar
                  locale="ja"
                  ref={calendarHolidayRef}
                  plugins={[dayGridPlugin, interactionPlugin, momentPlugin]}
                  initialView="dayGridMonth"
                  height="auto"
                  customButtons={{
                    CustomPrevMonth: {
                      text: <LeftOutlined />,
                      click: function () {
                        calendarHolidayRef.current.getApi().prev()
                        calendarNoTasteDayRef.current.getApi().prev()
                        setCalendarFrom(
                          moment(calendarHolidayRef.current.getApi().getDate())
                            .startOf("month")
                            .hours(0)
                            .minutes(0)
                            .format("YYYY-MM-DD HH:mm"),
                        )
                        setCalendarTo(
                          moment(calendarHolidayRef.current.getApi().getDate())
                            .endOf("month")
                            .hours(23)
                            .minutes(59)
                            .format("YYYY-MM-DD HH:mm"),
                        )
                      },
                    },
                    CustomNextMonth: {
                      text: <RightOutlined />,
                      click: function () {
                        calendarHolidayRef.current.getApi().next()
                        calendarNoTasteDayRef.current.getApi().next()
                        setCalendarFrom(
                          moment(calendarHolidayRef.current.getApi().getDate())
                            .startOf("month")
                            .hours(0)
                            .minutes(0)
                            .format("YYYY-MM-DD HH:mm"),
                        )
                        setCalendarTo(
                          moment(calendarHolidayRef.current.getApi().getDate())
                            .endOf("month")
                            .hours(23)
                            .minutes(59)
                            .format("YYYY-MM-DD HH:mm"),
                        )
                      },
                    },
                  }}
                  headerToolbar={{
                    left: "CustomPrevMonth",
                    center: "title",
                    right: "CustomNextMonth",
                  }}
                  titleFormat={(date) => {
                    return moment(date.date).format("YYYY年M月")
                  }}
                  firstDay={0}
                  unselectAuto={false}
                  showNonCurrentDates={false}
                  fixedWeekCount={false}
                  slotLabelFormat={{
                    hour: "2-digit",
                    minute: "2-digit",
                    omitZeroMinute: false,
                  }}
                  dayHeaderFormat={(date) => {
                    return moment(date.date).format("ddd")
                  }}
                  dayCellContent={(cell) => (
                    <>
                      <p className="text-sm mb-2">
                        {moment(cell.date).format("D日")}
                      </p>
                      {cell.date.getDay() === 2 || cell.date.getDay() === 3 ? (
                        <p className="text-center font-bold text-lg text-rose-600">
                          休
                        </p>
                      ) : (
                        ""
                      )}
                    </>
                  )}
                  events={holidays.map((holiday) => {
                    return {
                      groupId: "holiday",
                      scheduleId: holiday?.scheduleId,
                      start: moment(holiday?.scheduleDate).format("YYYY-MM-DD"),
                      backgroundColor: "#fff",
                      borderColor: "#FFF",
                    }
                  })}
                  eventContent={(content) => {
                    return (
                      <Row justify="center" align="middle" className="h-full">
                        <Col>
                          <ImCross className="font-medium text-lg text-rose-600" />
                        </Col>
                      </Row>
                    )
                  }}
                  eventClick={(eventInfo) => {
                    showDeleteHolidayModal(
                      eventInfo.event.start,
                      eventInfo.event.extendedProps.scheduleId,
                    )
                  }}
                  dateClick={(info) => {
                    if (
                      moment(info.date).day() !== 2 &&
                      moment(info.date).day() !== 3
                    ) {
                      if (
                        holidays.find((h) =>
                          moment(h?.scheduleDate).isSame(
                            moment(info.date),
                            "day",
                          ),
                        )
                      ) {
                        showDeleteHolidayModal(
                          info.date,
                          holidays.find((h) =>
                            moment(h?.scheduleDate).isSame(
                              moment(info.date),
                              "day",
                            ),
                          )?.scheduleId,
                        )
                      } else {
                        showCreateHolidayModal(info.date)
                      }
                    }
                  }}
                  slotEventOverlap={false}
                  displayEventTime={true}
                  displayEventEnd={false}
                  nowIndicator={true}
                />
              </CalendarWrapper>
              <Alert
                className="mt-4"
                message={
                  <p className="text-center">
                    店舗受け取り不可な日にクリックして休み日を設定してください
                  </p>
                }
                type="warning"
              />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card
              type="inner"
              title="イートイン不可な日の設定"
              className="mt-8"
            >
              <CalendarWrapper>
                <FullCalendar
                  locale="ja"
                  ref={calendarNoTasteDayRef}
                  plugins={[dayGridPlugin, interactionPlugin, momentPlugin]}
                  initialView="dayGridMonth"
                  height="auto"
                  customButtons={{
                    CustomPrevMonth: {
                      text: <LeftOutlined />,
                      click: function () {
                        calendarHolidayRef.current.getApi().prev()
                        calendarNoTasteDayRef.current.getApi().prev()
                        setCalendarFrom(
                          moment(
                            calendarNoTasteDayRef.current.getApi().getDate(),
                          )
                            .startOf("month")
                            .hours(0)
                            .minutes(0)
                            .format("YYYY-MM-DD HH:mm"),
                        )
                        setCalendarTo(
                          moment(
                            calendarNoTasteDayRef.current.getApi().getDate(),
                          )
                            .endOf("month")
                            .hours(23)
                            .minutes(59)
                            .format("YYYY-MM-DD HH:mm"),
                        )
                      },
                    },
                    CustomNextMonth: {
                      text: <RightOutlined />,
                      click: function () {
                        calendarHolidayRef.current.getApi().next()
                        calendarNoTasteDayRef.current.getApi().next()
                        setCalendarFrom(
                          moment(
                            calendarNoTasteDayRef.current.getApi().getDate(),
                          )
                            .startOf("month")
                            .hours(0)
                            .minutes(0)
                            .format("YYYY-MM-DD HH:mm"),
                        )
                        setCalendarTo(
                          moment(
                            calendarNoTasteDayRef.current.getApi().getDate(),
                          )
                            .endOf("month")
                            .hours(23)
                            .minutes(59)
                            .format("YYYY-MM-DD HH:mm"),
                        )
                      },
                    },
                  }}
                  headerToolbar={{
                    left: "CustomPrevMonth",
                    center: "title",
                    right: "CustomNextMonth",
                  }}
                  titleFormat={(date) => {
                    return moment(date.date).format("YYYY年M月")
                  }}
                  firstDay={0}
                  unselectAuto={false}
                  showNonCurrentDates={false}
                  fixedWeekCount={false}
                  slotLabelFormat={{
                    hour: "2-digit",
                    minute: "2-digit",
                    omitZeroMinute: false,
                  }}
                  dayHeaderFormat={(date) => {
                    return moment(date.date).format("ddd")
                  }}
                  dayCellContent={(cell) => (
                    <>
                      <p className="text-sm mb-2">
                        {moment(cell.date).format("D日")}
                      </p>
                      {cell.date.getDay() === 2 || cell.date.getDay() === 3 ? (
                        <p className="text-center font-bold text-lg text-rose-600">
                          休
                        </p>
                      ) : (
                        ""
                      )}
                    </>
                  )}
                  events={[
                    ...holidays.map((holiday) => {
                      return {
                        groupId: "holiday",
                        display: "background",
                        scheduleId: holiday?.scheduleId,
                        start: moment(holiday?.scheduleDate).format(
                          "YYYY-MM-DD",
                        ),
                        backgroundColor: "#fecdd3",
                        classNames: ["cursor-not-allowed"],
                        borderColor: "#FFF",
                      }
                    }),
                    ...noTasteDays.map((ntd) => {
                      return {
                        groupId: "notaste",
                        scheduleId: ntd?.scheduleId,
                        start: moment(ntd?.scheduleDate).format("YYYY-MM-DD"),
                        backgroundColor: holidays.find((h) =>
                          moment(h?.scheduleDate).isSame(
                            moment(ntd.scheduleDate),
                            "day",
                          ),
                        )
                          ? "#fecdd3"
                          : "#fff",
                        borderColor: "#FFF",
                      }
                    }),
                  ]}
                  eventContent={(content) => {
                    if (content?.event?.groupId === "notaste") {
                      return (
                        <div className="flex justify-center items-center">
                          <ImCross className="font-medium text-lg text-primary" />
                        </div>
                      )
                    }
                  }}
                  eventClick={(eventInfo) => {
                    if (eventInfo?.event?.groupId === "notaste") {
                      showDeleteNoTasteDayModal(
                        eventInfo.event.start,
                        eventInfo.event.extendedProps.scheduleId,
                      )
                    }
                  }}
                  dateClick={(info) => {
                    if (
                      moment(info.date).day() !== 2 &&
                      moment(info.date).day() !== 3 &&
                      !holidays.find((h) =>
                        moment(h?.scheduleDate).isSame(
                          moment(info.date),
                          "day",
                        ),
                      )
                    ) {
                      if (
                        noTasteDays.find((ntd) =>
                          moment(ntd?.scheduleDate).isSame(
                            moment(info.date),
                            "day",
                          ),
                        )
                      ) {
                        showDeleteNoTasteDayModal(
                          info.date,
                          holidays.find((h) =>
                            moment(h?.scheduleDate).isSame(
                              moment(info.date),
                              "day",
                            ),
                          )?.scheduleId,
                        )
                      } else {
                        showCreateNoTasteDayModal(info.date)
                      }
                    }
                  }}
                  slotEventOverlap={false}
                  displayEventTime={true}
                  displayEventEnd={false}
                  nowIndicator={true}
                />
              </CalendarWrapper>
              <Alert
                className="mt-4"
                message={
                  <p className="text-center">
                    店舗でイートイン不可な日にクリックして試飲不可な日を設定してください
                  </p>
                }
                type="warning"
              />
            </Card>
          </Col>
        </Row>
      </Card>
    </>
  )
}

export default Pickup
